// TMOS Plan Health Detail Page
// Date: January 13, 2026
// Purpose: Display detailed plan health information with objectives

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { HealthIndicator } from '@/components/business-performance/HealthIndicator';
import { ProgressBar } from '@/components/business-performance/ProgressBar';
import { ObjectiveCard } from '@/components/business-performance/ObjectiveCard';
import { businessPerformanceService } from '@/services/businessPerformanceService';
import { PlanHealthDetail } from '@/types/businessPerformance';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ChartBarIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

export const PlanHealthDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<PlanHealthDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadPlanHealthDetail();
    }
  }, [id]);

  const loadPlanHealthDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await businessPerformanceService.getPlanHealthDetail(id);
      setDetail(data);
    } catch (error: any) {
      console.error('Error loading plan health detail:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingPlanHealth'));
    } finally {
      setLoading(false);
    }
  };

  const handleObjectiveClick = (objectiveId: string) => {
    navigate(`/performance/objectives/${objectiveId}?plan_id=${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading plan health...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="text-center py-12">
            <p className="text-[var(--color-secondary-text)] mb-4">
              {t('businessPerformance.planNotFound') || 'Plan not found'}
            </p>
            <Button onClick={() => navigate('/performance')} variant="primary">
              {t('common.backToDashboard') || 'Back to Dashboard'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { plan, health, objectives } = detail;

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/performance')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t('common.back') || 'Back'}
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
                {plan.name}
              </h1>
              {plan.description && (
                <p className="text-[var(--color-secondary-text)] mb-4">
                  {plan.description}
                </p>
              )}
              <div className="flex items-center gap-4 text-sm text-[var(--color-secondary-text)]">
                {plan.plan_type && (
                  <span className="flex items-center gap-1">
                    <ChartBarIcon className="w-4 h-4" />
                    {plan.plan_type}
                  </span>
                )}
                {plan.plan_year && (
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="w-4 h-4" />
                    {plan.plan_year}
                  </span>
                )}
                {plan.status && (
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                    {plan.status}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="mb-2">
                <HealthIndicator
                  status={health.status}
                  score={health.health_score}
                  showScore={true}
                  size="lg"
                />
              </div>
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('businessPerformance.healthScore') || 'Health Score'}
              </p>
            </div>
          </div>
        </div>

        {/* Health Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card variant="outline">
            <CardContent className="p-4">
              <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                {t('businessPerformance.objectivesCount') || 'Total Objectives'}
              </p>
              <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                {health.objectives_count}
              </p>
            </CardContent>
          </Card>
          <Card variant="outline">
            <CardContent className="p-4">
              <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                {t('businessPerformance.objectivesOnTrack') || 'Objectives On Track'}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                  {health.objectives_on_track}
                </p>
                <HealthIndicator status="GREEN" size="sm" />
              </div>
            </CardContent>
          </Card>
          <Card variant="outline">
            <CardContent className="p-4">
              <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                {t('businessPerformance.objectivesAtRisk') || 'Objectives At Risk'}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                  {health.objectives_at_risk}
                </p>
                <HealthIndicator status="YELLOW" size="sm" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Time vs Objectives Progress */}
        <Card variant="outline" className="mb-6">
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {t('businessPerformance.timeVsObjectives') || 'Time vs Objectives Progress'}
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[var(--color-secondary-text)]">
                    {t('businessPerformance.timeProgress') || 'Time Progress'}
                  </span>
                  <span className="text-sm font-semibold text-[var(--color-surface-text)]">
                    {health.time_elapsed_percent.toFixed(1)}%
                  </span>
                </div>
                <ProgressBar
                  progress={health.time_elapsed_percent}
                  color="primary"
                  height="md"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[var(--color-secondary-text)]">
                    {t('businessPerformance.objectivesProgress') || 'Objectives Progress'}
                  </span>
                  <span className="text-sm font-semibold text-[var(--color-surface-text)]">
                    {health.objectives_progress_percent.toFixed(1)}%
                  </span>
                </div>
                <ProgressBar
                  progress={health.objectives_progress_percent}
                  color={health.status === 'GREEN' ? 'success' : health.status === 'YELLOW' ? 'warning' : 'danger'}
                  height="md"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Objectives List */}
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('businessPerformance.objectives') || 'Objectives'}
          </h2>
          {objectives && objectives.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {objectives.map((item) => (
                <ObjectiveCard
                  key={item.objective.id}
                  objective={item.objective}
                  progress={item.progress}
                  onClick={() => handleObjectiveClick(item.objective.id)}
                />
              ))}
            </div>
          ) : (
            <Card variant="outline">
              <CardContent className="p-8 text-center">
                <FlagIcon className="w-12 h-12 text-[var(--color-secondary-text)] mx-auto mb-4 opacity-50" />
                <p className="text-[var(--color-secondary-text)]">
                  {t('businessPerformance.noObjectives') || 'No objectives found for this plan.'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

